import { useState, useEffect, createContext, Children, useContext } from 'react';
import DicomLoader from './DicomLoader';
import { Slider, Dropdown, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons'
import './style.css';

import worker from './sharedWorker';

export const ImageSettingContext = createContext(null);
export const GeneralSettingsContext = createContext(null);

const windowParams = [
    { preset: 'DICOM' },
    { preset: 'Custom' },
    { preset: 'Bone',  center: 350, width: 2000 },
    { preset: 'Abdomen',  center: 50, width: 320 },
    { preset: 'Lung',  center: -500, width: 1500} ,
    { preset: 'Brain',  center: 35, width: 110 },
    { preset: 'Mediastinum',  center: 80, width: 400 },
    { preset: 'MIP',  center: 120, width: 380 },
];


const interpParams = ['Linear', 'Cubic']

export function ImageSettingProvider( { children} ){
    const [settings, setSettings] = useState({ window: windowParams[0] });

    function updateSettings(newval){
        setSettings(prev => ({...prev, ...newval}));
    }

    return(
        <ImageSettingContext.Provider value={{ settings, updateSettings }}>
            { children }
        </ImageSettingContext.Provider>
    )
}

export function GeneralSettingProvider( { children }){
    const [settings, setSettings] = useState({ interp:interpParams[0] });

    function updateSettings(newval){
        setSettings( prev => ({ ...prev, ...newval }));
    }

    return(
        <GeneralSettingsContext.Provider value={{ settings, updateSettings }}>
            { children }
        </GeneralSettingsContext.Provider>
    )
}


export function OptionsProvider({ children }){
    return(
        <ImageSettingProvider>
            <GeneralSettingProvider>
                { children }
            </GeneralSettingProvider>
        </ImageSettingProvider>
    )
}
 
function ImageSetting(){
    const windowParamsByPreset = Object.fromEntries(windowParams.map(wp => [wp.preset, wp]));
    const settingsContext = useContext(ImageSettingContext) ;
    const settings = settingsContext.settings ;
    const updateSettings = settingsContext.updateSettings ;

    const onPreset = ({ key }) => {
        if (key === 'Custom'){
            updateSettings({ window: { ...settings.window, preset: key }});
        } else updateSettings({ window: windowParamsByPreset[key] });
    };

    const presetItems = windowParams.map(val => ({ label:val.preset, key: val.preset }));

    return(
        <div>
            <Dropdown menu={{ items: presetItems, onClick: onPreset }}>
                <a onClick={e => e.preventDefault()}>
                    <Space>
                        Preset: {settings.window.preset}
                        <DownOutlined />
                    </Space>
                </a>
            </Dropdown>

            {settings.window.preset === 'Custom' &&
                <div>
                    Window: {settings.window.width}
                    <Slider defaultValue={settings.window.width} disabled={false} max={3074} min={-1024}
                        onChange={(value) => updateSettings({ window: { ...settings.window, width: value } })}
                    />
                    Level: {settings.window.center}
                    <Slider defaultValue={settings.window.center} disabled={false} max={3074} min={-1024}
                        onChange={(value) => updateSettings({ window: { ...settings.window, center: value } })}
                    />
                </div>
            }
        </div>
    );    

}
function Measurement(){
    return(
        <div>

        </div>
    );
}

function General(){
    const settingsContext = useContext(GeneralSettingsContext);
    const settings = settingsContext.settings;
    const updateSettings = settingsContext.updateSettings;

    const interpItems = interpParams.map(val => ({ label:val, key: val }));

    const onInterp = ({ key }) => {
        updateSettings({ interp: key });
    }


    return(

        <Dropdown menu={{ items: interpItems, onClick: onInterp }}>
            <a onClick={e => e.preventDefault()}>
                <Space>
                    Interpolation: {settings.interp}
                    <DownOutlined/>
                </Space>
            </a>
        </Dropdown>

    );
}

function Options(){
    
    const [activeTab, setActiveTab] = useState('Image')


    return(
        <div className='options-box'>
            <div className='options-tab-header'>
                <button className={activeTab === 'Image' ? 'active' : ''} onClick={()=> setActiveTab('Image')}>Image Settings</button>
                <button className={activeTab === 'Measure' ? 'active' : ''}onClick={()=> setActiveTab('Measure')}>Measurements</button>
                <button className={activeTab === 'General' ? 'active' : ''}onClick={()=> setActiveTab('General')}>General</button>

            </div>
            <div className='options-tab-content'>
                {activeTab === 'Image' && (<ImageSetting />)}
                {activeTab === 'Measure' && (<Measurement />)}
                {activeTab === 'General' && (<General />)}
            </div>
        </div>
    );
}


export default function Controls(){
    const [isFullscreen, setIsFullscreen] = useState(false);


    // Handles fullscreen mode toggling
    useEffect(() => {
        function handleFullscreenChange() {
        setIsFullscreen(!!document.fullscreenElement);
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);      

    return (
        <div className="controls-outer">
        {/* Branding section */}
        <div className='branding-loader-box'>
            <div className="dicom-branding">
                <div className="dicom-branding-row">
                <img
                    src="/logo512.png"
                    alt="App Logo"
                    className="dicom-logo"
                />
                <span className="dicomira-title">DICOMira</span>
                </div>
            </div>
            <DicomLoader/>
        </div>


        
        <Options />
        

        {/* Fullscreen toggle button */}
        {!isFullscreen && <div className="fullscreen" title="Go Fullscreen" onClick={() => document.documentElement.requestFullscreen()}>
            <svg width="32" height="32">
            <polyline points="0,12 0,0 12,0" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            <polyline points="20,0 32,0 32,12" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            <polyline points="0,20 0,32 12,32" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            <polyline points="20,32 32,32 32,20" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            </svg>
        </div>}


        {isFullscreen && <div className="fullscreen" title="Exit Fullscreen" onClick={() => document.exitFullscreen()}>
            <svg width="32" height="32">
            <polyline points="0,12 12,12 12,0" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            <polyline points="20,0 20,12 32,12" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            <polyline points="0,20 12,20 12,32" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            <polyline points="20,32 20,20 32,20" style={{ fill: 'none', stroke: '#888', strokeWidth: 5 }} />
            </svg>
        </div>}

        </div>
    );
}